import { useMemo } from 'react';
import { cn, getInitials } from '@/lib/utils';
import type { UserRole } from '@/types';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  firstName: string;
  lastName?: string | null;
  profilePictureUrl?: string | null;
  role?: UserRole;
  size?: AvatarSize;
  className?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-lg',
  xl: 'h-20 w-20 text-2xl',
};

const roleBackgroundColors: Record<UserRole, string> = {
  parent: 'bg-purple-500',
  caretaker: 'bg-blue-500',
  family: 'bg-amber-500',
  professional: 'bg-teal-500',
  admin: 'bg-amber-500',
};

export function Avatar({
  firstName,
  lastName,
  profilePictureUrl,
  role = 'family',
  size = 'md',
  className,
}: AvatarProps) {
  const initials = useMemo(
    () => getInitials(firstName, lastName),
    [firstName, lastName]
  );

  const bgColor = roleBackgroundColors[role];

  if (profilePictureUrl) {
    return (
      <img
        src={profilePictureUrl}
        alt={`${firstName} ${lastName || ''}`}
        className={cn(
          'rounded-full object-cover',
          sizeClasses[size],
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center font-semibold text-white',
        sizeClasses[size],
        bgColor,
        className
      )}
    >
      {initials}
    </div>
  );
}

export default Avatar;
